// Shared building blocks for the Attendance pages (Today, Daily, Monthly,
// Employee): status metadata, formatters, page header, KPI tile, a simple
// grid table, and common control styles. Built on the flat RABS design tokens.
import { useState, useCallback, useContext } from "react";
import { Box, Stack, Typography, Button, Chip, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Popover } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { AuthContext } from "../../context/AuthContext";
import { useToast } from "../../components/ToastProvider";
import { useUpdateAttendanceEmployee } from "../../hooks/queries";
import FlatCard from "../../components/FlatCard";
import TableSkeleton from "../../components/TableSkeleton";
import { COLORS, RADIUS } from "../../theme/tokens";
import { HEADING_FONT } from "../../theme/fonts";

// --- Status metadata --------------------------------------------------------
// `excel` = cell fill (hex, no #) used in exported sheets.
export const STATUS_META = {
  present:      { label: "Present",      short: "P",  color: "#15803D", bg: "#DCFCE7", excel: "DCFCE7" },
  half_day:     { label: "Half Day",     short: "HD", color: "#B45309", bg: "#FEF3C7", excel: "FEF3C7" },
  absent:       { label: "Absent",       short: "A",  color: "#B91C1C", bg: "#FEE2E2", excel: "FEE2E2" },
  missed_punch: { label: "Missed Punch", short: "MP", color: "#7C3AED", bg: "#EDE9FE", excel: "EDE9FE" },
  in_office:    { label: "Inside",       short: "IN", color: "#1D4ED8", bg: "#DBEAFE", excel: "DBEAFE" },
  outside:      { label: "Outside",      short: "OUT", color: "#C2410C", bg: "#FFEDD5", excel: "FFEDD5" },
  not_in:       { label: "Not In Yet",   short: "–",  color: "#64748B", bg: "#F1F5F9", excel: "F1F5F9" },
  weekly_off:   { label: "Weekly Off",   short: "WO", color: "#475569", bg: "#E2E8F0", excel: "E2E8F0" },
  holiday:      { label: "Holiday",      short: "H",  color: "#0E7490", bg: "#CFFAFE", excel: "CFFAFE" },
};

export const statusMeta = (s) =>
  STATUS_META[s] || { label: "—", short: "", color: COLORS.faint, bg: "transparent", excel: null };

// --- Formatters -------------------------------------------------------------
const pad = (n) => String(n).padStart(2, "0");
export const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const ym = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
export const parseYmd = (s) => {
  const [y, m, d] = String(s).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

// "2026-11-04 10:05:12" → "10:05 AM"
export function fmtTime(stamp) {
  if (!stamp) return "—";
  const [h, m] = stamp.slice(11, 16).split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  return `${pad(((h + 11) % 12) + 1)}:${pad(m)} ${ampm}`;
}

// 492 → "8h 12m"
export function fmtMinutes(min) {
  if (!min) return "—";
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h ? `${h}h ${pad(m)}m` : `${m}m`;
}

// "2026-11-04" → "Wed, 04 Nov 2026"
export function fmtDate(s, opts = { weekday: "short", day: "2-digit", month: "short", year: "numeric" }) {
  return parseYmd(s).toLocaleDateString("en-IN", opts);
}

// "2026-11" → "November 2026"
export const fmtMonth = (s) => parseYmd(`${s}-01`).toLocaleDateString("en-IN", { month: "long", year: "numeric" });

// "10:00" → "10:00 AM"
export const fmtClock = (hhmm) => fmtTime(`0000-00-00 ${hhmm}:00`);

export function timeAgo(iso) {
  if (!iso) return "never";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  return new Date(iso).toLocaleString("en-IN");
}

// --- Styles -----------------------------------------------------------------
export const fieldSx = {
  "& .MuiOutlinedInput-root": {
    background: COLORS.surface,
    borderRadius: RADIUS.input,
    fontSize: 13.5,
    "& fieldset": { borderColor: COLORS.hairlineStrong },
    "&:hover fieldset": { borderColor: COLORS.borderHover },
    "&.Mui-focused fieldset": { borderColor: COLORS.primary },
  },
  "& .MuiOutlinedInput-input": { py: "8px" },
};

// Same height for every header control (buttons, arrows, date fields).
export const CONTROL_HEIGHT = 38;
export const controlFieldSx = { ...fieldSx, "& .MuiOutlinedInput-root": { ...fieldSx["& .MuiOutlinedInput-root"], height: CONTROL_HEIGHT } };

export const primaryBtnSx = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: 13.5,
  px: 2,
  py: 0,
  height: 38,
  minHeight: 38,
  borderRadius: RADIUS.input,
  color: "#fff",
  background: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
  boxShadow: "0 4px 14px rgba(59, 130, 246, 0.30)",
  whiteSpace: "nowrap",
  "&:hover": { boxShadow: "0 8px 22px rgba(59, 130, 246, 0.40)" },
  "&.Mui-disabled": { color: "rgba(255,255,255,0.8)", opacity: 0.7 },
};

export const secondaryBtnSx = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: 13.5,
  px: 2,
  py: 0,
  height: 38,
  minHeight: 38,
  borderRadius: RADIUS.input,
  color: COLORS.ink,
  background: COLORS.surface,
  border: `1px solid ${COLORS.hairlineStrong}`,
  whiteSpace: "nowrap",
  "&:hover": { background: COLORS.tile, borderColor: COLORS.borderHover },
};

// Square arrow button (prev / next) matching the control height.
export const iconBtnSx = { ...secondaryBtnSx, px: 0, width: 38, minWidth: 38, flexShrink: 0 };

// --- Components -------------------------------------------------------------
export function StatusChip({ status, late, early }) {
  const m = statusMeta(status);
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
      <Chip
        size="small"
        label={m.label}
        sx={{ height: 22, fontSize: 11.5, fontWeight: 700, color: m.color, background: m.bg, borderRadius: RADIUS.pill }}
      />
      {late && (
        <Chip size="small" label="Late" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, color: COLORS.warning, background: COLORS.warningSoft }} />
      )}
      {early && (
        <Chip size="small" label="Early" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, color: "#9333EA", background: "#F3E8FF" }} />
      )}
    </Stack>
  );
}

// Punches may arrive as plain stamps (old shape) or { time, type }.
export const normalizePunches = (punches = []) =>
  punches.map((p, i) => (typeof p === "string" ? { time: p, type: i % 2 === 0 ? "in" : "out" } : p));

const PUNCH_STYLE = {
  in: { label: "IN", color: "#15803D", bg: "#DCFCE7", border: "#BBF7D0" },
  out: { label: "OUT", color: "#B91C1C", bg: "#FEE2E2", border: "#FECACA" },
};

// Full-day timeline: IN 10:24 → OUT 13:30 → IN 14:05 → ...
export function PunchTimeline({ punches, emptyText = "—" }) {
  const list = normalizePunches(punches);
  if (!list.length) return <Typography sx={{ fontSize: 13, color: COLORS.faint }}>{emptyText}</Typography>;
  return (
    <Stack direction="row" alignItems="center" sx={{ flexWrap: "wrap", gap: 0.5 }}>
      {list.map((p, i) => {
        const st = PUNCH_STYLE[p.type] || PUNCH_STYLE.in;
        return (
          <Stack key={`${p.time}-${i}`} direction="row" alignItems="center" spacing={0.5}>
            {i > 0 && <Typography sx={{ fontSize: 11, color: COLORS.faint }}>→</Typography>}
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 0.75, py: 0.2, borderRadius: "6px", background: st.bg, border: `1px solid ${st.border}`, whiteSpace: "nowrap" }}>
              <Typography component="span" sx={{ fontSize: 10, fontWeight: 800, color: st.color }}>{st.label}</Typography>
              <Typography component="span" sx={{ fontSize: 11.5, fontWeight: 600, color: COLORS.ink }}>{fmtTime(p.time)}</Typography>
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
}

// Compact cell: "OUT 11:54 · 4 punches ▾". Clicking opens a floating dropdown
// (popover) with the punch list — it overlays the table, so the row never grows.
export function PunchSummary({ punches, emptyText = "—" }) {
  const [anchor, setAnchor] = useState(null);
  const list = normalizePunches(punches);
  if (!list.length) return <Typography sx={{ fontSize: 13, color: COLORS.faint }}>{emptyText}</Typography>;
  const last = list[list.length - 1];
  const st = PUNCH_STYLE[last.type] || PUNCH_STYLE.in;
  const open = Boolean(anchor);
  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(open ? null : e.currentTarget);
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          px: 1,
          py: 0.4,
          border: `1px solid ${open ? COLORS.primary : COLORS.hairlineStrong}`,
          borderRadius: "8px",
          background: open ? COLORS.primarySoft : COLORS.surface,
          cursor: "pointer",
          font: "inherit",
          whiteSpace: "nowrap",
          "&:hover": { borderColor: COLORS.primary },
        }}
      >
        <Typography component="span" sx={{ fontSize: 10, fontWeight: 800, color: st.color }}>{st.label}</Typography>
        <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color: COLORS.ink }}>{fmtTime(last.time)}</Typography>
        <Typography component="span" sx={{ fontSize: 11.5, color: COLORS.muted }}>
          · {list.length} punch{list.length === 1 ? "" : "es"}
        </Typography>
        <Typography component="span" sx={{ fontSize: 11, color: COLORS.primary, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}>
          ▾
        </Typography>
      </Box>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{ paper: { sx: { mt: 0.75, p: 0.75, minWidth: anchor?.offsetWidth || 160, maxHeight: 320, borderRadius: "10px", border: `1px solid ${COLORS.hairlineStrong}`, boxShadow: "0 10px 30px rgba(15,23,42,0.12)" } } }}
      >
        {list.map((p, i) => {
          const ps = PUNCH_STYLE[p.type] || PUNCH_STYLE.in;
          return (
            <Stack key={`${p.time}-${i}`} direction="row" alignItems="center" spacing={1} sx={{ py: 0.5, px: 0.75, borderTop: i ? `1px dashed ${COLORS.hairline}` : "none" }}>
              <Typography component="span" sx={{ fontSize: 10.5, color: COLORS.faint, width: 16, textAlign: "right" }}>{i + 1}.</Typography>
              <Box sx={{ px: 0.6, py: 0.1, borderRadius: "5px", background: ps.bg, border: `1px solid ${ps.border}`, minWidth: 34, textAlign: "center" }}>
                <Typography component="span" sx={{ fontSize: 10, fontWeight: 800, color: ps.color }}>{ps.label}</Typography>
              </Box>
              <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 600, color: COLORS.ink }}>{fmtTime(p.time)}</Typography>
            </Stack>
          );
        })}
      </Popover>
    </>
  );
}

// Which table rows are expanded (Set of row keys) + a toggle.
export function useExpandedRows() {
  const [keys, setKeys] = useState(() => new Set());
  const toggle = useCallback((key) => {
    setKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  return { keys, toggle };
}

// Expanded row panel: full IN/OUT timeline + inside sessions.
export function PunchDetailPanel({ row }) {
  const sessions = row.sessions || [];
  return (
    <Box sx={{ px: 2.5, py: 1.75, background: COLORS.tile, borderTop: `1px dashed ${COLORS.hairlineStrong}` }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: "uppercase", letterSpacing: "0.5px", mb: 1 }}>
        Full day — IN / OUT
      </Typography>
      <PunchTimeline punches={row.punches} />
      {sessions.length > 0 && (
        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 2, mt: 1.5 }}>
          {sessions.map((s, i) => (
            <Typography key={`${s.in}-${i}`} sx={{ fontSize: 12, color: COLORS.muted }}>
              <b style={{ color: COLORS.ink }}>Session {i + 1}:</b> {fmtTime(s.in)} – {s.out ? fmtTime(s.out) : s.open ? "now" : "no OUT"} ({fmtMinutes(s.minutes)}
              {s.open ? ", still inside" : ""})
            </Typography>
          ))}
        </Stack>
      )}
    </Box>
  );
}

// First punch of the day (time string) or null.
export const firstPunchOf = (r) => normalizePunches(r.punches)[0]?.time || r.firstIn || null;
// Last punch of the day — only when there are 2+ punches (else it equals the first).
export const lastPunchOf = (r) => {
  const list = normalizePunches(r.punches);
  return list.length > 1 ? list[list.length - 1].time : null;
};
// Punches between the first and the last (the in-day IN/OUTs).
export const middlePunches = (punches) => normalizePunches(punches).slice(1, -1);

export const timelineText = (punches) =>
  normalizePunches(punches)
    .map((p) => `${p.type === "out" ? "OUT" : "IN"} ${fmtTime(p.time)}`)
    .join(" → ");

// "3 IN · 2 OUT"
export function InOutCount({ inCount = 0, outCount = 0 }) {
  if (!inCount && !outCount) return <Typography sx={{ fontSize: 13, color: COLORS.faint }}>—</Typography>;
  return (
    <Typography sx={{ fontSize: 12.5, fontWeight: 700, whiteSpace: "nowrap" }}>
      <Box component="span" sx={{ color: PUNCH_STYLE.in.color }}>{inCount} IN</Box>
      <Box component="span" sx={{ color: COLORS.faint }}> · </Box>
      <Box component="span" sx={{ color: PUNCH_STYLE.out.color }}>{outCount} OUT</Box>
    </Typography>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      alignItems={{ xs: "stretch", md: "center" }}
      spacing={2}
      sx={{ mb: 3, justifyContent: "space-between" }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h4" sx={{ fontFamily: HEADING_FONT, fontWeight: 700, color: COLORS.ink, letterSpacing: "-0.5px" }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: COLORS.muted, mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", gap: 1, justifyContent: { xs: "flex-start", md: "flex-end" } }}>
          {actions}
        </Stack>
      )}
    </Stack>
  );
}

// center: icon + number centred in the card (used when the card is stretched tall).
export function KpiCard({ label, value, sub, accent, icon, onClick, active, center }) {
  const clickable = Boolean(onClick);
  return (
    <FlatCard
      hover={clickable}
      accent={accent}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (clickable && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{ p: 2.25, ...(center && { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }), ...(active && { borderColor: accent, boxShadow: `0 0 0 2px ${accent}33` }) }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: "12px",
            background: `${accent}14`,
            color: accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& .MuiSvgIcon-root": { fontSize: 22 },
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 22, fontWeight: 800, color: COLORS.ink, lineHeight: 1.1 }}>
            {value}
          </Typography>
          <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: COLORS.muted, textTransform: "uppercase", letterSpacing: "0.5px", mt: 0.25 }}>
            {label}
          </Typography>
        </Box>
      </Stack>
      {sub && <Typography sx={{ fontSize: 12, color: COLORS.faint, mt: 1 }}>{sub}</Typography>}
    </FlatCard>
  );
}

export function SectionTitle({ children, right }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${COLORS.hairline}` }}>
      <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 15, fontWeight: 600, color: COLORS.ink }}>{children}</Typography>
      {right}
    </Stack>
  );
}

// Grid-based table matching the rest of the app.
//   columns: [{ key, label, width ("1fr" | "120px"), align, render(row, i) }]
//   expandedKeys + renderExpanded: optional panel shown under an expanded row.
export function DataTable({ columns, rows, loading, onRowClick, emptyIcon, emptyText = "No records", rowKey = (r) => r.employeeCode, expandedKeys, renderExpanded }) {
  const template = columns.map((c) => c.width || "1fr").join(" ");
  // Every row uses the SAME width (full card width, never narrower than the sum
  // of the column minimums) so columns line up perfectly from row to row.
  const minTableWidth =
    columns.reduce((sum, c) => sum + (Number(String(c.width || "").match(/(d+)px/)?.[1]) || 80), 0) +
    16 * (columns.length - 1) + 40;
  const headCellSx = {
    fontFamily: HEADING_FONT,
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: COLORS.muted,
  };
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box sx={{ minWidth: minTableWidth }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: template,
          gap: 2,
          px: 2.5,
          py: 1.5,
          alignItems: "center",
          borderBottom: `1px solid ${COLORS.hairline}`,
          background: COLORS.tile,
        }}
      >
        {columns.map((c) => (
          <Typography key={c.key} sx={{ ...headCellSx, textAlign: c.align || "center" }}>
            {c.label}
          </Typography>
        ))}
      </Box>

      {loading ? (
        <TableSkeleton rows={6} columns={columns.length} showAvatar={false} />
      ) : rows.length === 0 ? (
        <Box sx={{ py: 7, textAlign: "center", color: COLORS.muted }}>
          {emptyIcon}
          <Typography sx={{ fontSize: 13.5, mt: 1 }}>{emptyText}</Typography>
        </Box>
      ) : (
        rows.map((row, i) => {
          const key = rowKey(row, i);
          const open = Boolean(expandedKeys?.has(key));
          return (
            <Box key={key} sx={{ borderBottom: `1px solid ${COLORS.hairline}`, "&:last-child": { borderBottom: "none" } }}>
              <Box
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={{
                  display: "grid",
                  gridTemplateColumns: template,
                  gap: 2,
                  px: 2.5,
                  py: 1.4,
                  alignItems: "center",
                  cursor: onRowClick ? "pointer" : "default",
                  fontSize: 13.5,
                  fontFamily: (t) => t.typography.fontFamily,
                  color: COLORS.ink,
                  background: open ? COLORS.tile : "transparent",
                  "&:hover": { background: COLORS.tile },
                }}
              >
                {columns.map((c) => (
                  <Box key={c.key} sx={{ minWidth: 0, display: "flex", alignItems: "center", textAlign: c.align || "center", justifyContent: { left: "flex-start", right: "flex-end" }[c.align] || "center" }}>
                    {c.render ? c.render(row, i) : row[c.key] ?? "—"}
                  </Box>
                ))}
              </Box>
              {open && renderExpanded && renderExpanded(row)}
            </Box>
          );
        })
      )}
      </Box>
    </Box>
  );
}

// Employee cell: avatar initials + name + code. Pass `onEdit` to show a ✏️
// button (or "Add name" when the code has no name yet).
export function EmployeeCell({ name, code, department, onEdit }) {
  const initials = String(name || code || "?")
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: RADIUS.pill,
          background: COLORS.primarySoft,
          color: COLORS.primaryDark,
          fontSize: 11.5,
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {initials}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.25}>
          <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, color: name ? COLORS.ink : COLORS.muted }}>
            {name || `Code ${code}`}
          </Typography>
          {onEdit && name && (
            <Tooltip title="Edit name">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
                sx={{ p: 0.25, color: COLORS.faint, "&:hover": { color: COLORS.primary } }}
              >
                <EditOutlinedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography noWrap sx={{ fontSize: 11.5, color: COLORS.faint }}>
            #{code}
            {department ? ` · ${department}` : ""}
          </Typography>
          {onEdit && !name && (
            <Box
              component="button"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              sx={{ border: 0, p: 0, background: "none", cursor: "pointer", font: "inherit", fontSize: 11.5, fontWeight: 700, color: COLORS.primary }}
            >
              + Add name
            </Box>
          )}
        </Stack>
      </Box>
    </Stack>
  );
}

// Name/department editor. Usage:
//   const editor = useEmployeeEditor();
//   <EmployeeCell ... onEdit={editor.canEdit ? () => editor.open(row) : undefined} />
//   {editor.dialog}
export function useEmployeeEditor() {
  const { hasPermission } = useContext(AuthContext);
  const canEdit = hasPermission("attendance", "edit");
  const toast = useToast();
  const save = useUpdateAttendanceEmployee();
  const [emp, setEmp] = useState(null);
  const [name, setName] = useState("");
  const [department, setDepartment] = useState("");

  const open = useCallback((row) => {
    setEmp(row);
    setName(row.name || "");
    setDepartment(row.department || "");
  }, []);
  const close = () => setEmp(null);

  async function submit() {
    try {
      await save.mutateAsync({ code: emp.employeeCode, data: { name: name.trim(), department: department.trim() } });
      toast.success(`Saved #${emp.employeeCode}`);
      close();
    } catch (err) {
      toast.error(err.message || "Failed to save");
    }
  }

  const dialog = (
    <Dialog open={Boolean(emp)} onClose={close} fullWidth maxWidth="xs" onClick={(e) => e.stopPropagation()}>
      <DialogTitle sx={{ fontFamily: HEADING_FONT, fontWeight: 700, fontSize: 17 }}>
        Employee #{emp?.employeeCode}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField label="Name" size="small" autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} inputProps={{ maxLength: 150 }} />
          <TextField label="Department (optional)" size="small" value={department} onChange={(e) => setDepartment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} inputProps={{ maxLength: 100 }} />
          <Typography sx={{ fontSize: 12, color: COLORS.faint }}>
            Biometric code #{emp?.employeeCode} — this name shows on every attendance page and export.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close} sx={secondaryBtnSx}>Cancel</Button>
        <Button onClick={submit} disabled={save.isPending} sx={primaryBtnSx}>
          {save.isPending ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return { canEdit, open, dialog };
}

export function FilterChips({ options, value, onChange }) {
  return (
    <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
      {options.map((o) => {
        const active = value === o.key;
        return (
          <Chip
            key={o.key}
            label={o.count !== undefined ? `${o.label} (${o.count})` : o.label}
            onClick={() => onChange(o.key)}
            sx={{
              fontWeight: 600,
              fontSize: 12.5,
              color: active ? "#fff" : COLORS.muted,
              background: active ? "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)" : COLORS.surface,
              border: `1px solid ${active ? "transparent" : COLORS.hairlineStrong}`,
              "&:hover": { background: active ? "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)" : COLORS.tile },
            }}
          />
        );
      })}
    </Stack>
  );
}

// colors (optional): { [status]: { solid } } — solid badge style (used by the calendar).
export function StatusLegend({ keys = ["present", "half_day", "absent", "missed_punch", "weekly_off", "holiday"], colors, lateColor }) {
  return (
    <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1.5 }}>
      {keys.map((k) => {
        const m = STATUS_META[k];
        return (
          <Tooltip key={k} title={m.label}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Box sx={{ minWidth: 26, height: 20, px: 0.5, borderRadius: "6px", background: colors?.[k]?.solid || m.bg, color: colors?.[k] ? "#fff" : m.color, fontSize: 10.5, fontWeight: 800, fontFamily: (t) => t.typography.fontFamily, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {m.short}
              </Box>
              <Typography sx={{ fontSize: 12, color: COLORS.muted }}>{m.label}</Typography>
            </Stack>
          </Tooltip>
        );
      })}
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: lateColor || COLORS.warning }} />
        <Typography sx={{ fontSize: 12, color: COLORS.muted }}>Late</Typography>
      </Stack>
    </Stack>
  );
}

export function ExportButton({ onClick, busy }) {
  return (
    <Button onClick={onClick} disabled={busy} sx={secondaryBtnSx}>
      {busy ? "Exporting…" : "Export Excel"}
    </Button>
  );
}

// Rows → plain objects for Excel (shared by Today + Daily).
export function dailyRowsForExcel(rows) {
  return rows.map((r, i) => ({
    sn: i + 1,
    code: r.employeeCode,
    name: r.name || "",
    department: r.department || "",
    in: firstPunchOf(r) ? fmtTime(firstPunchOf(r)) : "",
    out: lastPunchOf(r) ? fmtTime(lastPunchOf(r)) : "",
    inCount: r.inCount ?? "",
    outCount: r.outCount ?? "",
    work: r.workMinutes ? fmtMinutes(r.workMinutes) : "",
    outside: r.outsideMinutes ? fmtMinutes(r.outsideMinutes) : "",
    timeline: r.punches ? timelineText(r.punches) : "",
    late: r.isLate ? fmtMinutes(r.lateMinutes) : "",
    early: r.isEarly ? fmtMinutes(r.earlyMinutes) : "",
    status: statusMeta(r.status).label,
    _status: r.status,
  }));
}

export const DAILY_EXCEL_COLUMNS = [
  { header: "S.No", key: "sn", width: 7, align: "center" },
  { header: "Emp Code", key: "code", width: 11, align: "center" },
  { header: "Name", key: "name", width: 24 },
  { header: "Department", key: "department", width: 16 },
  { header: "First Punch", key: "in", width: 12, align: "center" },
  { header: "Last Punch", key: "out", width: 12, align: "center" },
  { header: "IN Count", key: "inCount", width: 9, align: "center" },
  { header: "OUT Count", key: "outCount", width: 10, align: "center" },
  { header: "Inside Time", key: "work", width: 12, align: "center" },
  { header: "Outside Time", key: "outside", width: 12, align: "center" },
  { header: "Late By", key: "late", width: 10, align: "center" },
  { header: "Early By", key: "early", width: 10, align: "center" },
  { header: "Status", key: "status", width: 13, align: "center" },
  { header: "Full Day Timeline", key: "timeline", width: 60 },
];

export const dailyCellFill = (row, col) =>
  col?.key === "status" ? statusMeta(row._status).excel : col?.key === "late" && row.late ? "FEF3C7" : null;

// Error banner for when the attendance API fails (usually the SmartOffice
// biometric PC is off / unreachable). Shows the reason + a Retry button.
export function AttendanceError({ error, onRetry, retrying }) {
  if (!error) return null;
  const unreachable = /unreachable|timeout|network|SmartOffice/i.test(error.message || "");
  return (
    <Box sx={{ mb: 2.5, p: 2, borderRadius: "12px", border: "1px solid #FECACA", background: "#FEF2F2", display: "flex", alignItems: { xs: "flex-start", sm: "center" }, flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#B91C1C" }}>
          {unreachable ? "Unable to connect to the biometric server" : "Failed to load attendance data"}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: "#7F1D1D", mt: 0.25 }}>
          {unreachable
            ? "The SmartOffice PC (192.168.1.150:82) is turned off or not on the network. Check the PC and its IIS service, then click Retry."
            : error.message}
        </Typography>
      </Box>
      <Button onClick={onRetry} disabled={retrying} sx={{ ...primaryBtnSx, background: "#DC2626", "&:hover": { background: "#B91C1C" } }}>
        {retrying ? "Retrying…" : "Retry"}
      </Button>
    </Box>
  );
}
